import React from 'react';
import { School, Users, GraduationCap, Heart, BookOpen, Activity, TrendingUp, AlertTriangle, AlertOctagon, XCircle, Eye, Info, CheckCircle2, Clock, Server, RefreshCw, Tag } from 'lucide-react';
import { motion } from 'framer-motion';
import { PageLayout } from '../../shared/PageLayout';
import { StatCard } from '../../shared/StatCard';
import { ConnectionBadge } from '../shared/ConnectionBadge';
import { QualityRing, ProgressBar } from './dashboard/DashboardCharts';
import { containerVariants, itemVariants } from '../shared/animationVariants';
import { STORAGE_QUOTA_MB } from '../../../utils/storageQuota';
import { useDashboardData } from './dashboard/useDashboardData';
export function DashboardPage({
  onNavigate
}: {
  onNavigate?: (view: string) => void;
}) {
  const {
    stats,
    contentStats,
    pipelineLoading,
    strictModeOn,
    handleRefreshPipeline,
    passCount,
    warnCount,
    failCount,
    avgScore,
    totalValidated,
    severityCounts,
    systemHealth,
    recentActivity
  } = useDashboardData();
  return <PageLayout title="Dashboard" subtitle="Overzicht van het Meester djay platform" actions={<ConnectionBadge status="live" data-id="element-6856" data-mp="xxgghuwfwczd" />} data-id="element-6855" data-mp="t3uf4yvq7viv">
      
      {/* Section 1: Platform Overview */}
      <motion.div variants={containerVariants} initial="hidden" animate="show" className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 mb-6" data-id="element-6857" data-mp="k5s9g611sep9">
        
        <motion.div variants={itemVariants} data-id="element-6858" data-mp="1bvwn7luay3hi">
          <StatCard label="Leerlingen" value={stats.totalLearners} icon={<Users className="w-4 h-4 text-blue-500" data-id="element-6860" data-mp="1gv9ld2qmiwze" />} data-id="element-6859" data-mp="1bw6edcr32gos" />
          
        </motion.div>
        <motion.div variants={itemVariants} data-id="element-6861" data-mp="12v8jpl2jbliv">
          <StatCard label="Docenten" value={stats.totalTeachers} icon={<GraduationCap className="w-4 h-4 text-indigo-500" data-id="element-6863" data-mp="17tjuby0nm025" />} data-id="element-6862" data-mp="12u6ywovpvkny" />
          
        </motion.div>
        <motion.div variants={itemVariants} data-id="element-6864" data-mp="3nlh7ipawuldg">
          <StatCard label="Ouders" value={stats.totalParents} icon={<Heart className="w-4 h-4 text-rose-500" data-id="element-6866" data-mp="3skts13eb7gcv" />} data-id="element-6865" data-mp="3njmkn5g2wj6q" />
          
        </motion.div>
        <motion.div variants={itemVariants} data-id="element-6867" data-mp="1y3so0f5pmke5">
          <StatCard label="Scholen" value={stats.totalSchools} icon={<School className="w-4 h-4 text-purple-500" data-id="element-6869" data-mp="1t58vtx0iwcdc" />} data-id="element-6868" data-mp="1y3k96hq1150b" />
          
        </motion.div>
      </motion.div>

      {/* Section 2: Content & Quality */}
      <motion.div variants={containerVariants} initial="hidden" animate="show" className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 sm:p-5 mb-6" data-id="element-6870" data-mp="1ocfvrmg01s7l">
        
        <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4" data-id="element-6871" data-mp="1felya86i0siu">
          Content & Kwaliteit
        </h3>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-6872" data-mp="1agkhxv0gj7b2">
          {/* Left: Content Stats */}
          <div className="space-y-4" data-id="element-6873" data-mp="15hhcwakh9133">
            <div className="flex items-center justify-between" data-id="element-6874" data-mp="11eb6n2xd79wm">
              <div className="flex items-center gap-2" data-id="element-6875" data-mp="11g5uoq3xni4x">
                <BookOpen className="w-4 h-4 text-blue-500" data-id="element-6876" data-mp="2zse6duqtsmax" />
                <span className="text-sm font-medium text-gray-700" data-id="element-6877" data-mp="2vs3rttoywnkg">
                  Vragenbank
                </span>
              </div>
              <span className="text-lg font-bold text-gray-900" data-id="element-6878" data-mp="hj9x90j2jegr">
                {contentStats.totalQuestions.toLocaleString('nl')}
              </span>
            </div>
            <div className="flex items-center justify-between" data-id="element-6879" data-mp="r9cpgyid8oe1">
              <div className="flex items-center gap-2" data-id="element-6880" data-mp="rbz4tkbi086t">
                <Tag className="w-4 h-4 text-purple-500" data-id="element-6881" data-mp="vaeq9jrxl8i4" />
                <span className="text-sm font-medium text-gray-700" data-id="element-6882" data-mp="3uql6jg59rk7p">
                  Families
                </span>
              </div>
              <span className="text-sm text-gray-600" data-id="element-6883" data-mp="3lbkfzkebq3d">
                {contentStats.totalFamilies} / {contentStats.taxonomyFamilies}
              </span>
            </div>
            <ProgressBar value={contentStats.coveragePct} color={contentStats.coveragePct >= 80 ? 'bg-emerald-500' : contentStats.coveragePct >= 50 ? 'bg-amber-500' : 'bg-red-500'} label="Seed dekking" data-id="element-6884" data-mp="ckq7sq59nkjn" />
            
            <ProgressBar value={contentStats.visualCoveragePct} color={contentStats.visualCoveragePct >= 80 ? 'bg-emerald-500' : contentStats.visualCoveragePct >= 50 ? 'bg-amber-500' : 'bg-red-500'} label={`Visuele componenten (${contentStats.usedVisualsCount}/${contentStats.registeredVisuals})`} data-id="element-6885" data-mp="1nzc7rkobj19t" />
            
          </div>

          {/* Right: Quality & Validation */}
          <div className="space-y-4" data-id="element-6886" data-mp="1rib118fbit6a">
            <div className="flex items-center justify-between" data-id="element-6887" data-mp="1rh9p71qha2ni">
              <div className="flex items-center gap-3" data-id="element-6888" data-mp="25hke3z889761">
                {avgScore !== null ? <QualityRing score={avgScore} data-id="element-6889" data-mp="25fhjt301krvi" /> : <div className="w-12 h-12 rounded-full bg-gray-100 animate-pulse" data-id="element-6890" data-mp="20jiyxpdx9rzu" />}
                <div data-id="element-6891" data-mp="2f7dpaz4gjk9r">
                  <div className="text-sm font-medium text-gray-700" data-id="element-6892" data-mp="2f6lu3pervok0">
                    Kwaliteitsscore
                  </div>
                  <div className="text-xs text-gray-500" data-id="element-6893" data-mp="2a9c86jqldv0x">
                    alle {contentStats.totalQuestions.toLocaleString('nl')}{' '}
                    vragen
                  </div>
                </div>
              </div>
              <button onClick={handleRefreshPipeline} disabled={pipelineLoading} className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-40" title="Validatie vernieuwen" data-id="element-6894" data-mp="32v0urospkf1d">
                
                <RefreshCw className={`w-4 h-4 ${pipelineLoading ? 'animate-spin' : ''}`} data-id="element-6895" data-mp="2ttklxlhjkkz9" />
                
              </button>
            </div>

            {pipelineLoading ? <div className="flex gap-4" data-id="element-6896" data-mp="2k411wci3el2t">
                {['Geslaagd', 'Review', 'Afgekeurd'].map(l => <div key={l} className="flex-1 bg-gray-50 rounded-lg p-2 animate-pulse" data-id="element-6897" data-mp="2ouzkjiqjzv98">
                
                    <div className="h-5 bg-gray-200 rounded mb-1" data-id="element-6898" data-mp="2a6v8uypx4jzy" />
                    <div className="h-3 bg-gray-100 rounded w-2/3" data-id="element-6899" data-mp="2a4ihjuy9sxa8" />
                  </div>)}
              </div> : passCount !== null ? <>
                <div className="flex gap-3" data-id="element-6900" data-mp="215exoora4duo">
                  <div className="flex-1 bg-emerald-50 rounded-lg p-2.5 text-center" data-id="element-6901" data-mp="217rqjwyks8mi">
                    <div className="text-lg font-bold text-emerald-700" data-id="element-6902" data-mp="2648asg2nm2ub">
                      {passCount}
                    </div>
                    <div className="text-xs text-emerald-600" data-id="element-6903" data-mp="6xt9plpxk5aw">Geslaagd</div>
                  </div>
                  <div className="flex-1 bg-amber-50 rounded-lg p-2.5 text-center" data-id="element-6904" data-mp="1yq4o19y9z2x">
                    <div className="text-lg font-bold text-amber-700" data-id="element-6905" data-mp="1wnd3ct5fxw3">
                      {warnCount}
                    </div>
                    <div className="text-xs text-amber-600" data-id="element-6906" data-mp="unxdpu71672f">Review</div>
                  </div>
                  <div className="flex-1 bg-red-50 rounded-lg p-2.5 text-center" data-id="element-6907" data-mp="pokhwjy6lpnp">
                    <div className="text-lg font-bold text-red-700" data-id="element-6908" data-mp="lmpftd8ff88v">
                      {failCount}
                    </div>
                    <div className="text-xs text-red-600" data-id="element-6909" data-mp="gofjv0evo3m7">Afgekeurd</div>
                  </div>
                </div>
                <div className="h-2 bg-gray-100 rounded-full overflow-hidden flex" data-id="element-6910" data-mp="36dzrjmix2p2l">
                  <div className="h-full bg-emerald-500" style={{
                width: `${totalValidated > 0 ? (passCount ?? 0) / totalValidated * 100 : 0}%`
              }} data-id="element-6911" data-mp="31dlpcl3zy9ch" />
                
                  <div className="h-full bg-amber-400" style={{
                width: `${totalValidated > 0 ? (warnCount ?? 0) / totalValidated * 100 : 0}%`
              }} data-id="element-6912" data-mp="1lu4bhxnmx3zv" />
                
                  <div className="h-full bg-red-500" style={{
                width: `${totalValidated > 0 ? (failCount ?? 0) / totalValidated * 100 : 0}%`
              }} data-id="element-6913" data-mp="1unr63kg3w17h" />
                
                </div>
              </> : <div className="text-sm text-gray-400 text-center py-4" data-id="element-6914" data-mp="24kcb45s4lcbb">
                Validatie wordt geladen...
              </div>}
          </div>
        </div>
      </motion.div>

      {/* Section 2b — Phase 1: 5-level severity breakdown across ALL issues.
                   The 3 tiles above count QUESTIONS (verdict per question, always
                   3 outcomes). These 5 tiles count ISSUES by display-severity. */}
      <motion.div variants={containerVariants} initial="hidden" animate="show" className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 sm:p-5 mb-6" data-id="element-6915" data-mp="j8c7ydc5v3as">
        
        <div className="flex items-center justify-between mb-3" data-id="element-6916" data-mp="3rh2117oh7072">
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-6917" data-mp="9443f7f5ojg2">
            Bevindingen per ernst
          </h3>
          <span className="text-[10px] text-gray-400" data-id="element-6918" data-mp="53ld5hswvlfz">
            5-niveau model · per bevinding
          </span>
        </div>
        {severityCounts === null ? <div className="grid grid-cols-2 sm:grid-cols-5 gap-2" data-id="element-6919" data-mp="38j8xq49xwo95">
            {Array.from({
          length: 5
        }).map((_, i) => <div key={i} className="h-12 bg-gray-50 rounded-lg animate-pulse" data-id="element-6920" data-mp="3mja9ierqtg3e" data-mpi={i} />)}
          </div> : <div className="grid grid-cols-2 sm:grid-cols-5 gap-2" data-id="element-6921" data-mp="3hjfimb7ywqic">
            {[{
          key: 'critical' as const,
          label: 'Kritiek',
          Icon: AlertOctagon,
          bg: 'bg-red-50',
          border: 'border-red-200',
          text: 'text-red-700',
          iconClass: 'text-red-600',
          hint: 'Blocker · ernst ≥90 (student-facing risico)'
        }, {
          key: 'blocker' as const,
          label: 'Blocker',
          Icon: XCircle,
          bg: 'bg-orange-50',
          border: 'border-orange-200',
          text: 'text-orange-700',
          iconClass: 'text-orange-600',
          hint: 'Blocker · ernst 70-89'
        }, {
          key: 'review' as const,
          label: 'Review',
          Icon: Eye,
          bg: 'bg-yellow-50',
          border: 'border-yellow-200',
          text: 'text-yellow-700',
          iconClass: 'text-yellow-600',
          hint: 'Coach · ernst ≥45 (twijfel, vraagt menselijk oordeel)'
        }, {
          key: 'coach' as const,
          label: 'Coach',
          Icon: AlertTriangle,
          bg: 'bg-blue-50',
          border: 'border-blue-200',
          text: 'text-blue-700',
          iconClass: 'text-blue-600',
          hint: 'Coach · ernst <45 (auteursfeedback)'
        }, {
          key: 'info' as const,
          label: 'Info',
          Icon: Info,
          bg: 'bg-slate-50',
          border: 'border-slate-200',
          text: 'text-slate-700',
          iconClass: 'text-slate-500',
          hint: 'Observatie zonder actie'
        }].map(tier => {
          const count = severityCounts[tier.key] ?? 0;
          const TierIcon = tier.Icon;
          return <div key={tier.key} className={`flex items-center gap-2 p-2.5 rounded-lg border ${tier.bg} ${tier.border}`} title={tier.hint} data-id="element-6922" data-mp="kh2uq9xbwqig">
                
                  <TierIcon className={`w-4 h-4 flex-shrink-0 ${tier.iconClass}`} data-id="element-6923" data-mp="alrnsds5wnl9" />
                
                  <div className="min-w-0 flex-1" data-id="element-6924" data-mp="3szqtqar45cqa">
                    <p className={`text-lg font-bold leading-none ${tier.text}`} data-id="element-6925" data-mp="3sww9okc0htxe">
                    
                      {count.toLocaleString('nl-NL')}
                    </p>
                    <p className={`text-[10px] font-medium ${tier.text} mt-0.5`} data-id="element-6926" data-mp="3j1baetybv7rc">
                    
                      {tier.label}
                    </p>
                  </div>
                </div>;
        })}
          </div>}
        <p className="text-[10px] text-gray-400 mt-2 leading-relaxed" data-id="element-6927" data-mp="2zereya5g1et1">
          <span className="font-semibold" data-id="element-6928" data-mp="2zgucdf8xea7s">Verdict</span> (boven) telt vragen — 3
          uitkomsten van de gate. <span className="font-semibold" data-id="element-6929" data-mp="3e9xkkw0mnblu">Ernst</span>{' '}
          (hier) telt individuele bevindingen — één vraag kan meerdere
          bevindingen op verschillende niveaus hebben.
        </p>
      </motion.div>

      {/* Section 3: Activity & System Health */}
      <motion.div variants={containerVariants} initial="hidden" animate="show" className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 mb-6" data-id="element-6930" data-mp="3icu72xtafrv2">
        
        {/* Recent Activity — F-A3 (19 May 2026): timestamps in this list
             are synthetic (computed as `now - fixed offset`). Counts and
             messages ARE real, but the times are not — show a mock badge
             so admins don't read this as an audit-grade activity log.
             Audit §11 / §19. */}
        <motion.div variants={itemVariants} className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 sm:p-5" data-id="element-6931" data-mp="d06k0t5e5312">
          
          <div className="flex items-center justify-between mb-4" data-id="element-6932" data-mp="hyzvyh514lz4">
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider flex items-center gap-2" data-id="element-6933" data-mp="mxb66sz8kyhv">
              <Clock className="w-4 h-4" data-id="element-6934" data-mp="rp327j15vwfe" /> Recente Activiteit
            </h3>
            <ConnectionBadge status="mock" label="Demo-tijden" data-id="element-6935" data-mp="vqo98nr0lgho" />
          </div>
          <div className="space-y-3" data-id="element-6936" data-mp="10opr55cpf9rk">
            {recentActivity.map((item, i) => <div key={i} className="flex items-start gap-3" data-id="element-6937" data-mp="10r2ho6wj2sg8" data-mpi={i}>
                <div className={`mt-0.5 flex-shrink-0 ${item.type === 'success' ? 'text-emerald-500' : item.type === 'warning' ? 'text-amber-500' : 'text-blue-500'}`} data-id="element-6938" data-mp="15pdtgn6dvd13">
                
                  {item.type === 'success' ? <CheckCircle2 className="w-4 h-4" data-id="element-6939" data-mp="37h0dg4ruy7o2" /> : item.type === 'warning' ? <AlertTriangle className="w-4 h-4" data-id="element-6940" data-mp="3ce0l2k4cviha" /> : <Activity className="w-4 h-4" data-id="element-6941" data-mp="3cf2358jsh58i" />}
                </div>
                <div className="min-w-0 flex-1" data-id="element-6942" data-mp="1tkfsl6d8b63b">
                  <p className="text-sm text-gray-700 leading-snug" data-id="element-6943" data-mp="1tl8spohomxch">
                    {item.message}
                  </p>
                  <p className="text-xs text-gray-400 mt-0.5 font-mono" data-id="element-6944" data-mp="1kqkn1x873dn5">
                    {item.time}
                  </p>
                </div>
              </div>)}
          </div>
        </motion.div>

        {/* System Health */}
        <motion.div variants={itemVariants} className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 sm:p-5" data-id="element-6945" data-mp="2cgywnh96e3t0">
          
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-6946" data-mp="28ekfd1l5nc3u">
            <Server className="w-4 h-4" data-id="element-6947" data-mp="1yihb9q6qt3e9" /> Systeemgezondheid
          </h3>
          <div className="space-y-4" data-id="element-6948" data-mp="1ykle04swnwcy">
            <div className="flex items-center justify-between" data-id="element-6949" data-mp="s2y8xv1ccqm1">
              <span className="text-sm text-gray-600" data-id="element-6950" data-mp="s0c4i49qyr81">Strict Mode</span>
              <span className={`text-xs font-bold px-2 py-1 rounded-full ${strictModeOn ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-500'}`} data-id="element-6951" data-mp="n1r0dtrdjneb">
                
                {strictModeOn ? 'AAN' : 'UIT'}
              </span>
            </div>
            <div className="flex items-center justify-between" data-id="element-6952" data-mp="21zlhcl2abax5">
              <span className="text-sm text-gray-600" data-id="element-6953" data-mp="21x8ubtho6ad8">Gem. responstijd</span>
              <span className="text-sm font-semibold text-gray-800" data-id="element-6954" data-mp="26yy5d94w6o1b">
                {systemHealth.responseTime}ms
              </span>
            </div>
            <div data-id="element-6955" data-mp="2ypcdeoq85652">
              <div className="flex items-center justify-between mb-1.5" data-id="element-6956" data-mp="2ypvy2d79e8ms">
                <span className="text-sm text-gray-600" data-id="element-6957" data-mp="2kq3a2pn4a6ii">Opslag</span>
                <span className="text-sm font-semibold text-gray-800" data-id="element-6958" data-mp="2krxkvb4oci8v">
                  {systemHealth.storageMb} / {STORAGE_QUOTA_MB} MB
                </span>
              </div>
              <div className="h-2 bg-gray-100 rounded-full overflow-hidden" data-id="element-6959" data-mp="1993812aywdba">
                <div className="h-full bg-violet-500 rounded-full" style={{
                width: `${systemHealth.storagePct}%`
              }} data-id="element-6960" data-mp="1eask8l9xex0y" />
                
              </div>
            </div>
            <div data-id="element-6961" data-mp="11q3q7135mtu1">
              <div className="flex items-center justify-between mb-1.5" data-id="element-6962" data-mp="11qn7qgow7g7j">
                <span className="text-sm text-gray-600" data-id="element-6963" data-mp="1fr6ap9ei3w2u">Actief deze week</span>
                <span className="text-sm font-semibold text-gray-800" data-id="element-6964" data-mp="1as3ac29gue17">
                  {stats.activeThisWeek} leerlingen
                </span>
              </div>
              <div className="h-2 bg-gray-100 rounded-full overflow-hidden" data-id="element-6965" data-mp="n069f67k16ev">
                <div className="h-full bg-blue-500 rounded-full" style={{
                width: `${Math.min(100, stats.activeThisWeek / Math.max(stats.totalLearners, 1) * 100)}%`
              }} data-id="element-6966" data-mp="j28hvwqba88b" />
                
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>

      {/* Section 4: Quick Insights */}
      <motion.div variants={containerVariants} initial="hidden" animate="show" className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4" data-id="element-6967" data-mp="3vic3zn4iw83p">
        
        {/* Subscriptions */}
        <motion.div variants={itemVariants} className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 sm:p-5" data-id="element-6968" data-mp="1zd3c5ziq3ay5">
          
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4" data-id="element-6969" data-mp="2947fu32y79do">
            Abonnementen
          </h3>
          <div className="space-y-2.5" data-id="element-6970" data-mp="2e2ir8i8w5ry0">
            {stats.planDistribution.map(item => <div key={item.label} data-id="element-6971" data-mp="1ggv5qsdz4u19">
                <div className="flex justify-between text-xs mb-1" data-id="element-6972" data-mp="1ghwdamvs6ye8">
                  <span className="text-gray-600" data-id="element-6973" data-mp="1lg7n4xm2t8wg">{item.label}</span>
                  <span className="font-semibold text-gray-800" data-id="element-6974" data-mp="1qehj3afmkdj4">
                    {item.count}
                  </span>
                </div>
                <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden" data-id="element-6975" data-mp="1uhe93m7hvbx3">
                  <div className={`h-full rounded-full ${item.color}`} style={{
                width: `${item.pct}%`
              }} data-id="element-6976" data-mp="31sd0yqxwz0e1" />
                
                </div>
              </div>)}
          </div>
        </motion.div>

        {/* Top Learners */}
        <motion.div variants={itemVariants} className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 sm:p-5" data-id="element-6977" data-mp="q61diov501bz">
          
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4 flex items-center gap-1.5" data-id="element-6978" data-mp="lejc5zp7byqh">
            <TrendingUp className="w-3.5 h-3.5" data-id="element-6979" data-mp="gfgfb2jdkytl" /> Top Leerlingen
          </h3>
          {stats.topLearners.length > 0 ? <div className="space-y-2.5" data-id="element-6980" data-mp="bigbyzdwua66">
              {stats.topLearners.map((item, i) => <div key={i} className="flex items-center gap-2.5" data-id="element-6981" data-mp="6hahox4lqeuj" data-mpi={i}>
                  <div className="w-6 h-6 rounded-full bg-violet-100 text-violet-700 text-xs font-bold flex items-center justify-center flex-shrink-0" data-id="element-6982" data-mp="2fyokhmirbhl">
                    {i + 1}
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-6983" data-mp="1wv18lzssu1d4">
                    <p className="text-xs font-medium text-gray-800 truncate" data-id="element-6984" data-mp="1wtq41xrj4pky">
                      {item.name}
                    </p>
                  </div>
                  <span className="text-xs font-bold text-violet-600" data-id="element-6985" data-mp="10psu1jk0miu6">
                    {item.xp.toLocaleString('nl-NL')} XP
                  </span>
                </div>)}
            </div> : <p className="text-xs text-gray-400" data-id="element-6986" data-mp="r0rd9r5nbxf5">Geen leerlingen gevonden</p>}
        </motion.div>
      </motion.div>
    </PageLayout>;
}